'use client'

import * as React from 'react'
import Image from 'next/image'
import { ArrowRight } from 'lucide-react'
import {
  LazyMotion,
  domAnimation,
  m,
  useReducedMotion,
  useMotionValue,
  useSpring,
  useTransform,
} from 'framer-motion'
import { Button } from '@/components/ui/button'
import { TrackedLink } from '@/components/marketing/tracked-link'
import { RotatingHeadline, STATEMENTS, HOLD_MS, FADE_MS } from './rotating-headline'
import { DashboardMockup } from './dashboard-mockup'
import {
  InteractiveShoppableHero,
  type HeroSlide,
} from '@/components/marketing/interactive-shoppable-hero'
import { EASE } from '@/lib/motion'

// One set of merchant-facing proof pills per headline variant.
// Indexed to match STATEMENTS in rotating-headline.tsx.
// Technical metrics (Lighthouse, CLS, widget size) are real Idukki product
// figures. Engagement benchmarks (6.9×, 92%) cite Stackla/Nosto consumer
// survey and Nielsen Trust Advertising report respectively.
const TRUST_SETS: { k: string; v: string }[][] = [
  // 0 — "Turn your UGC into measured store revenue."
  [
    { k: 'Incremental', v: 'CVR' },
    { k: 'Full', v: 'attribution' },
    { k: 'Per-SKU', v: 'analytics' },
    { k: 'Live', v: 'revenue data' },
  ],
  // 1 — "The complete UGC marketing platform for your ecommerce store."
  [
    { k: '6', v: 'social sources' },
    { k: 'All', v: 'features' },
    { k: '1', v: 'platform' },
    { k: 'Zero', v: 'vendor sprawl' },
  ],
  // 2 — "Shoppable videos in 37 KB and a single embed."
  [
    { k: '37 KB', v: 'widget' },
    { k: '+90', v: 'Lighthouse' },
    { k: 'Negligible', v: 'CLS impact' },
    { k: '4 min', v: 'setup' },
  ],
  // 3 — "Embed authentic UGC that converts more visitors on your website."
  [
    { k: 'Higher', v: 'conversion rate' },
    { k: '6.9×', v: 'engagement' },
    { k: '92%', v: 'trust UGC' },
    { k: 'No-code', v: 'embed' },
  ],
  // 4 — "Every photo on your store, shoppable yours by default."
  [
    { k: '1-click', v: 'checkout' },
    { k: 'Auto', v: 'rights mgmt' },
    { k: 'AI', v: 'product tagging' },
    { k: 'All', v: 'sources' },
  ],
  // 5 — "Every UGC video turned into quick checkouts."
  [
    { k: 'Higher', v: 'video CVR' },
    { k: '6.9×', v: 'engagement' },
    { k: '1-click', v: 'add to cart' },
    { k: 'Auto', v: 'shoppable tags' },
  ],
  // 6 — "Stop paying four vendors for one job, start paying one, for the whole job."
  [
    { k: '4→1', v: 'vendors' },
    { k: 'Flat', v: 'pricing' },
    { k: 'Zero', v: 'overages' },
    { k: 'All', v: 'features every plan' },
  ],
]

// Mount choreography: the left column settles in on a stagger; the phone
// rises and settles a beat later. Above the fold, so it plays on load
// (initial -> animate), not on scroll. Reduced motion renders the end
// state instantly.
//
// Opacity stays at 1 throughout (only the y-offset animates). The hero H1
// and phone mockup are the LCP candidates on this page; Framer Motion's SSR
// output applies the `hidden` state's style inline, so an `opacity: 0`
// initial state paints nothing until JS hydrates and the animation runs —
// under CPU/network throttling that gated LCP behind hydration and pushed
// it to 7s+. A pure transform settle keeps the motion without hiding content.
const col = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } },
}
const item = {
  hidden: { opacity: 1, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: EASE } },
}

export function HeroContent({ slides, realHero }: { slides: HeroSlide[]; realHero?: string }) {
  const reduce = useReducedMotion()

  // Rotation state lives here so the headline and metrics strip stay in sync.
  const [index, setIndex] = React.useState(0)
  const [fading, setFading] = React.useState(false)

  React.useEffect(() => {
    if (reduce) return
    if (typeof window === 'undefined') return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const id = window.setInterval(() => {
      setFading(true)
      window.setTimeout(() => {
        setIndex((i) => (i + 1) % STATEMENTS.length)
        setFading(false)
      }, FADE_MS)
    }, HOLD_MS)
    return () => window.clearInterval(id)
  }, [reduce])

  // Subtle pointer-parallax on the phone (<= 8px). Motion values don't
  // re-render React, so this stays cheap. Disabled under reduced motion.
  const mx = useMotionValue(0)
  const my = useMotionValue(0)
  const sx = useSpring(mx, { stiffness: 120, damping: 20 })
  const sy = useSpring(my, { stiffness: 120, damping: 20 })
  const px = useTransform(sx, [-0.5, 0.5], [10, -10])
  const py = useTransform(sy, [-0.5, 0.5], [8, -8])

  const onMove = (e: React.MouseEvent) => {
    if (reduce) return
    const r = e.currentTarget.getBoundingClientRect()
    mx.set((e.clientX - r.left) / r.width - 0.5)
    my.set((e.clientY - r.top) / r.height - 0.5)
  }

  const trust = TRUST_SETS[index]

  return (
    <LazyMotion features={domAnimation}>
      <section className="svg-canvas relative overflow-hidden" onMouseMove={onMove}>
        <div
          aria-hidden
          className="pointer-events-none absolute -right-[10%] -top-[15%] -z-10 h-[55rem] w-[55rem] rounded-full opacity-[0.5] blur-3xl"
          style={{
            background: 'radial-gradient(circle, hsl(var(--primary) / 0.16), transparent 65%)',
          }}
        />

        <div className="container-wide grid items-center gap-14 pb-24 pt-24 md:pb-32 md:pt-36 lg:grid-cols-12 lg:gap-10">
          <m.div
            className="lg:col-span-6"
            variants={col}
            initial={reduce ? false : 'hidden'}
            animate={reduce ? false : 'show'}
          >
            <m.div variants={item}>
              <RotatingHeadline
                index={index}
                fading={fading}
                className="font-display-hero max-w-[15ch] text-balance text-5xl font-medium tracking-tight sm:text-6xl md:text-7xl lg:text-7xl"
              />
            </m.div>

            <m.p
              variants={item}
              className="mt-7 max-w-[34rem] text-pretty text-[1.15rem] leading-[1.65] text-muted-foreground"
            >
              Shoppable Instagram and TikTok videos and galleries, with AI product tagging, A/B
              testing and rights management: the full social commerce stack in one platform.
            </m.p>

            <m.div
              variants={item}
              className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-4"
              data-surface="home-hero"
            >
              <Button asChild size="lg">
                <TrackedLink
                  href="https://app.idukki.io/register"
                  event="sign_up_start"
                  surface="home-hero"
                >
                  Get started, no card <ArrowRight className="h-4 w-4" />
                </TrackedLink>
              </Button>
              <TrackedLink
                href="https://app.apollo.io/#/meet/idukki/30-min-1"
                event="book_demo_click"
                surface="home-hero"
                className="group inline-flex items-center gap-1.5 text-sm font-semibold text-foreground transition-colors hover:text-primary"
              >
                Book a demo
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </TrackedLink>
            </m.div>

            {/* Metrics strip — fades in sync with the rotating headline so
                each set of pills reinforces the active message variant. */}
            <m.dl variants={item} className="mt-12 flex flex-wrap items-center gap-2.5 text-sm">
              {trust.map((t) => (
                <div
                  key={`${index}-${t.k}`}
                  className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-card/60 px-3.5 py-1.5"
                  style={{
                    opacity: fading ? 0 : 1,
                    transition: `opacity ${FADE_MS}ms ease-in-out`,
                  }}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />
                  <dt className="font-semibold text-foreground">{t.k}</dt>
                  <dd className="text-muted-foreground">{t.v}</dd>
                </div>
              ))}
            </m.dl>
          </m.div>

          <div className="lg:col-span-6 lg:-mr-6 xl:-mr-16">
            <m.div
              style={reduce ? undefined : { x: px, y: py }}
              initial={reduce ? false : { opacity: 1, y: 28 }}
              animate={reduce ? false : { opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
            >
              {slides.length > 0 ? (
                <InteractiveShoppableHero slides={slides} brandLabel="idukki · live preview" />
              ) : realHero ? (
                <div className="relative overflow-hidden rounded-3xl border shadow-2xl shadow-primary/10">
                  <Image
                    src={realHero}
                    alt="Idukki product"
                    width={1200}
                    height={900}
                    className="h-auto w-full"
                    priority
                  />
                </div>
              ) : (
                <DashboardMockup />
              )}
            </m.div>
          </div>
        </div>
        <div className="container-wide">
          <div className="hairline" />
        </div>
      </section>
    </LazyMotion>
  )
}
